import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonIcon from '@mui/icons-material/Person'
import AddIcon from '@mui/icons-material/Add'
import { User } from '../../../types'
import { PermissionCard } from '../common'
import {
  PermissionCheckboxList,
  TeamPermissions,
  DEFAULT_PERMISSIONS,
  PERMISSION_LABELS,
} from './PermissionCheckboxList'
import { useState } from 'react'

// State için user bilgisi ile birlikte override yapısı
export interface UserOverrideState {
  user: { id: string; name?: string; email?: string }
  permissions: TeamPermissions
}

interface UserPermissionsProps {
  userOverrides: { [userId: string]: UserOverrideState }
  members: User[]
  onToggleUserPermission: (
    userId: string,
    key: keyof TeamPermissions,
    value: boolean,
  ) => void
  onAddUserOverride: (userId: string) => void
  onRemoveUserOverride: (userId: string) => void
  onClearAll: () => void
  isMobile?: boolean
}

export function UserPermissions({
  userOverrides,
  members,
  onToggleUserPermission,
  onAddUserOverride,
  onRemoveUserOverride,
  onClearAll,
  isMobile = false,
}: UserPermissionsProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>('')

  const totalUserOverrides = Object.keys(userOverrides || {}).length

  // Henüz override eklenmemiş kullanıcılar
  const availableMembers = members.filter(
    (member) => !userOverrides[member.id],
  )

  const handleAddUser = () => {
    if (selectedUserId) {
      onAddUserOverride(selectedUserId)
      setSelectedUserId('')
    }
  }

  const handleUserSelectChange = (event: SelectChangeEvent<string>) => {
    setSelectedUserId(event.target.value)
  }

  return (
    <PermissionCard
      title="Kullanıcı Özel İzinleri"
      subtitle={`${totalUserOverrides} kullanıcı için özel izin tanımlı`}
      icon={<PersonIcon />}
      headerColor="info"
      height={isMobile ? 'auto' : '100%'}
    >
      <Box
        sx={{
          height: isMobile ? 'auto' : '100%',
          maxHeight: isMobile ? '500px' : '100%',
          overflow: 'auto',
          p: 1,
        }}
      >
        {/* Kullanıcı Ekleme */}
        {availableMembers.length > 0 && (
          <Box sx={{ p: 1, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Kullanıcı Seç</InputLabel>
                <Select
                  value={selectedUserId}
                  onChange={handleUserSelectChange}
                  input={<OutlinedInput label="Kullanıcı Seç" />}
                >
                  {availableMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.name || member.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="small"
                onClick={handleAddUser}
                disabled={!selectedUserId}
                startIcon={<AddIcon />}
              >
                Ekle
              </Button>
            </Box>
          </Box>
        )}

        {/* Tümünü Sil Butonu */}
        {totalUserOverrides > 0 && (
          <Box sx={{ p: 1, mb: 1 }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={onClearAll}
              fullWidth
            >
              Tüm Kullanıcı İzinlerini Sil
            </Button>
          </Box>
        )}

        {/* Kullanıcı Override'ları */}
        {totalUserOverrides === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Henüz özel kullanıcı izni tanımlanmamış
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Yukarıdan kullanıcı seçerek özel izin tanımlayabilirsiniz
            </Typography>
          </Box>
        ) : (
          Object.entries(userOverrides).map(([userId, overrideState]) => {
            const { user, permissions } = overrideState
            // Sabit izin sayısı kullan
            const TOTAL_PERMISSION_COUNT = Object.keys(PERMISSION_LABELS).length
            const enabledCount = (Object.keys(PERMISSION_LABELS) as (keyof TeamPermissions)[])
              .filter((key) => permissions[key] === true).length

            return (
              <Accordion key={userId} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                      {user.name || user.email || 'Bilinmeyen Kullanıcı'}
                    </Typography>
                    <Chip
                      label={`${enabledCount}/${TOTAL_PERMISSION_COUNT} aktif`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Box
                    sx={{
                      p: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => onRemoveUserOverride(userId)}
                      fullWidth
                    >
                      Bu kullanıcının özel izinlerini kaldır
                    </Button>
                  </Box>
                  <Box
                    sx={{
                      maxHeight: isMobile ? 'auto' : '250px',
                      overflow: 'auto',
                    }}
                  >
                    <PermissionCheckboxList
                      permissions={permissions}
                      onChange={(key, value) =>
                        onToggleUserPermission(userId, key, value)
                      }
                      showCategories={false}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            )
          })
        )}
      </Box>
    </PermissionCard>
  )
}
